import React, { Suspense, useEffect, useRef, useState } from 'react';

import { useCart } from '../../contexts/CartContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import logoSmSvg from '../brand/faves-logo-sm.svg';
import { CloseIcon } from '../icons/Close';
import { HamburgerIcon } from '../icons/Hamburger';
import './SiteNav.css';
import { type NavLink } from '../../components/SiteNav/types';

// Lazy-load so marketing pages (cart disabled) don't ship the panel
// component and its CSS in their main bundle.
const CartPanel = React.lazy(() =>
    import('./CartPanel').then((m) => ({ default: m.CartPanel })),
);

// Redesign-era marketing links. Hidden on the shop/PDP surfaces when the
// splash_redesign flag is off (see redesignEnabled below).
const REDESIGN_LINKS: ReadonlyArray<NavLink> = [
    { label: 'Roster', href: '/roster', group: 'primary' },
    { label: 'For athletes', href: '/for-athletes', group: 'primary' },
    { label: 'For brands', href: '/for-brands', group: 'primary' },
];

const ACCOUNT_LINKS: ReadonlyArray<NavLink> = [
    { label: 'Apply', href: 'https://faves.xyz/apply/64eb8975-a4e5-4f29-bcb7-1d84e62df971', group: 'account' },
    { label: 'Log in', href: '/portal/login', group: 'account' },
];

const DEFAULT_LINKS: ReadonlyArray<NavLink> = [...REDESIGN_LINKS, ...ACCOUNT_LINKS];

function CartIcon() {
    return (
        <svg
            className="site-nav__icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path
                transform="translate(0 1.5)"
                d="M7.52441 17.1436C8.62896 17.1436 9.52441 18.039 9.52441 19.1436C9.52422 20.2479 8.62884 21.1435 7.52441 21.1436C6.41994 21.1436 5.52461 20.248 5.52441 19.1436C5.52441 18.039 6.41982 17.1436 7.52441 17.1436ZM19.5244 17.1436C20.629 17.1436 21.5244 18.039 21.5244 19.1436C21.5242 20.2479 20.6288 21.1435 19.5244 21.1436C18.4199 21.1436 17.5246 20.248 17.5244 19.1436C17.5244 18.039 18.4198 17.1436 19.5244 17.1436ZM4.19043 0C4.73497 0 5.2044 0.384136 5.31152 0.917969L5.92871 4L6.38574 6.28613L7.79492 13.334H19.3291L21.3428 6.28613H7.98633L7.5 4H22.8574C23.2159 4 23.5536 4.16903 23.7695 4.45508C23.9853 4.74134 24.0545 5.11235 23.9561 5.45703L21.2891 14.79C21.1489 15.2804 20.7015 15.619 20.1914 15.6191H6.85742C6.31287 15.6191 5.8444 15.235 5.7373 14.7012L3.25293 2.28613H2.28613C1.02374 2.28613 0 1.26239 0 0H4.19043Z"
                fill="currentColor"
            />
        </svg>
    );
}

type SiteNavProps = {
    links?: ReadonlyArray<NavLink>;
    // When false, the redesign-era marketing links (roster, for-athletes,
    // for-brands) are dropped, leaving only account links. Gated behind the
    // splash_redesign flag on the shop/PDP surfaces. Ignored when an explicit
    // `links` array is passed.
    redesignEnabled?: boolean;
    // When true, nav floats fixed at the top of the viewport, hides on
    // scroll-down past a threshold, and re-appears on scroll-up or when the
    // pointer is near the top edge. Also gains a dark top gradient once
    // scrolled. Pages using this must reserve top space themselves (PDP's
    // .pdp-main padding-top is enough; roster/shop add a body padding).
    floating?: boolean;
};

const SCROLL_DELTA_THRESHOLD = 5;
const HIDE_AFTER_PX = 80;

export function SiteNav({ links, redesignEnabled = true, floating = false }: SiteNavProps) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const lastScrollRef = useRef(0);
    const navRef = useRef<HTMLElement | null>(null);
    // Captured at click time so focus can be restored on close. Child
    // CartPanel grabs focus in its own mount effect, which runs before any
    // useEffect here, so document.activeElement is unreliable from this side.
    const prevFocusRef = useRef<HTMLElement | null>(null);

    const {
        isEnabled: cartEnabled,
        isOpen: cartOpen,
        cartCount,
        openCart,
        closeCart,
    } = useCart();

    const handleOpenCart = (e: React.MouseEvent<HTMLButtonElement>) => {
        // Only remember the trigger for focus restoration when the cart was
        // opened via keyboard (click detail === 0). Restoring focus after a
        // mouse-opened cart makes Safari paint a focus-visible ring on the
        // icon when it closes, which reads as a stray "selected" box.
        prevFocusRef.current = e.detail === 0 ? e.currentTarget : null;
        openCart();
    };

    const resolvedLinks = links ?? (redesignEnabled ? DEFAULT_LINKS : ACCOUNT_LINKS);
    const primaryLinks = resolvedLinks.filter((l) => (l.group ?? 'primary') === 'primary');
    const accountLinks = resolvedLinks.filter((l) => l.group === 'account');

    useEffect(() => {
        if (cartOpen && mobileMenuOpen) {
            setMobileMenuOpen(false);
        }
    }, [cartOpen, mobileMenuOpen]);

    useBodyScrollLock(cartOpen);

    useEffect(() => {
        if (!cartOpen) { return; }

        // Mark page content outside this nav as inert so keyboard / screen
        // reader users stay scoped to the cart dialog. <nav> isn't usually a
        // direct child of <body> (React mounts it deep in the page tree),
        // so we walk up the ancestor chain and inert each level's siblings;
        // never the ancestors themselves (inert inherits to descendants).
        // Track only the elements we touched so we don't strip a pre-existing
        // inert on cleanup.
        const newlyInert: Element[] = [];
        let walker: Element | null = navRef.current;
        while (walker !== null && walker !== document.body) {
            const parent = walker.parentElement;
            if (parent === null) { break; }
            for (const sibling of Array.from(parent.children)) {
                if (sibling === walker) { continue; }
                if (sibling.hasAttribute('inert')) { continue; }
                sibling.setAttribute('inert', '');
                newlyInert.push(sibling);
            }
            walker = parent;
        }

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { closeCart(); }
        };
        document.addEventListener('keydown', onKey);

        return () => {
            for (const el of newlyInert) { el.removeAttribute('inert'); }
            document.removeEventListener('keydown', onKey);
            const target = prevFocusRef.current;
            prevFocusRef.current = null;
            if (target !== null && target.isConnected) { target.focus(); }
        };
    }, [cartOpen, closeCart]);

    // Cart-open must force the nav back into view even if the user scrolled
    // it off-screen. Otherwise the cart panel (a child of <nav>) opens
    // off-screen and the user just sees a dimmed page with no visible cart.
    useEffect(() => {
        if (cartOpen) { setHidden(false); }
    }, [cartOpen]);

    useEffect(() => {
        if (!floating) { return; }
        lastScrollRef.current = window.scrollY;
        setScrolled(window.scrollY > 0);

        const onScroll = () => {
            const cur = window.scrollY;
            const delta = cur - lastScrollRef.current;
            if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) { return; }
            setScrolled(cur > 0);
            // Cart-open is a modal state; never hide the nav (it owns the
            // close X for desktop).
            if (cartOpen) {
                setHidden(false);
            } else if (delta > 0 && cur > HIDE_AFTER_PX) {
                setHidden(true);
            } else {
                setHidden(false);
            }
            lastScrollRef.current = cur;
        };
        const onMouseMove = (e: MouseEvent) => {
            if (e.clientY < HIDE_AFTER_PX) { setHidden(false); }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('mousemove', onMouseMove);
        return () => {
            window.removeEventListener('scroll', onScroll);
            document.removeEventListener('mousemove', onMouseMove);
        };
    }, [floating, cartOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) { return; }
        const onDocumentClick = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setMobileMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setMobileMenuOpen(false); }
        };
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [mobileMenuOpen]);

    const navClasses = [
        'site-nav',
        mobileMenuOpen ? 'site-nav--menu-open' : '',
        cartOpen ? 'site-nav--cart-open' : '',
        cartEnabled ? 'site-nav--cart-enabled' : '',
        floating ? 'site-nav--floating' : '',
        floating && scrolled ? 'site-nav--scrolled' : '',
        floating && hidden ? 'site-nav--hidden' : '',
    ].filter(Boolean).join(' ');

    const handleMobileToggleClick = () => {
        if (cartOpen) {
            closeCart();
            return;
        }
        setMobileMenuOpen((v) => !v);
    };

    return (
        <nav className={navClasses} ref={navRef}>
            {cartEnabled && cartOpen && (
                <div
                    className="site-nav__cart-overlay"
                    onClick={closeCart}
                    aria-hidden="true"
                />
            )}
            <div className="site-nav__bar">
                <a href="/" className="site-nav__chip site-nav__chip--logo" aria-label="Faves home">
                    <img src={logoSmSvg} alt="Faves" />
                </a>
                <div className="site-nav__right">
                    {primaryLinks.length > 0 && (
                        <div className="site-nav__chip site-nav__chip--links site-nav__chip--primary">
                            {primaryLinks.map(({ label, href }) => (
                                <a key={label} href={href} className="site-nav__link">{label}</a>
                            ))}
                        </div>
                    )}

                    {accountLinks.length > 0 && (
                        <div className="site-nav__chip site-nav__chip--links site-nav__chip--account">
                            {accountLinks.map(({ label, href }) => (
                                <a key={label} href={href} className="site-nav__link">{label}</a>
                            ))}
                            {cartEnabled && (
                                <button
                                    type="button"
                                    className="site-nav__link site-nav__cart-trigger"
                                    onClick={handleOpenCart}
                                    aria-label="Open cart"
                                >
                                    <span>CART</span>
                                    {cartCount > 0 && (
                                        <span className="site-nav__cart-badge site-nav__cart-badge--inline">{cartCount}</span>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {cartEnabled && cartOpen && (
                        <div className="site-nav__chip site-nav__chip--cart-control">
                            <span className="site-nav__link site-nav__cart-label">CART</span>
                            {cartCount > 0 && (
                                <span className="site-nav__cart-badge site-nav__cart-badge--inline">{cartCount}</span>
                            )}
                            <button
                                type="button"
                                className="site-nav__icon-btn site-nav__icon-btn--cart-close"
                                onClick={closeCart}
                                aria-label="Close cart"
                            >
                                <CloseIcon className="site-nav__icon" />
                            </button>
                        </div>
                    )}

                    {cartEnabled ? (
                        <div className="site-nav__chip site-nav__chip--mobile-cluster">
                            <button
                                type="button"
                                className="site-nav__icon-btn site-nav__icon-btn--cart"
                                onClick={handleOpenCart}
                                aria-label="Open cart"
                            >
                                <CartIcon />
                                {cartCount > 0 && (
                                    <span className="site-nav__cart-badge site-nav__cart-badge--floating">{cartCount}</span>
                                )}
                            </button>
                            <button
                                type="button"
                                className="site-nav__icon-btn site-nav__icon-btn--mobile-menu"
                                onClick={handleMobileToggleClick}
                                aria-label={cartOpen ? 'Close cart' : (mobileMenuOpen ? 'Close menu' : 'Open menu')}
                                aria-expanded={mobileMenuOpen}
                            >
                                <span className={`site-nav__icon-swap${cartOpen ? ' site-nav__icon-swap--close' : ''}`}>
                                    <span className="site-nav__icon-swap-slot site-nav__icon-swap-slot--hamburger">
                                        <HamburgerIcon className="site-nav__icon" />
                                    </span>
                                    <span className="site-nav__icon-swap-slot site-nav__icon-swap-slot--close">
                                        <CloseIcon className="site-nav__icon" />
                                    </span>
                                </span>
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="site-nav__chip site-nav__chip--hamburger"
                            onClick={() => setMobileMenuOpen((v) => !v)}
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileMenuOpen}
                        >
                            <HamburgerIcon className="site-nav__icon" />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={`site-nav__mobile-menu${mobileMenuOpen ? ' site-nav__mobile-menu--open' : ''}`}
                aria-hidden={!mobileMenuOpen}
            >
                {resolvedLinks.map(({ label, href }) => (
                    <a
                        key={label}
                        href={href}
                        className="site-nav__link"
                        tabIndex={mobileMenuOpen ? 0 : -1}
                    >
                        {label}
                    </a>
                ))}
            </div>
            {cartEnabled && cartOpen && (
                <Suspense fallback={null}>
                    <CartPanel />
                </Suspense>
            )}
        </nav>
    );
}
